import { resolveImageUrl } from '@/shared/utils/imageUtils';
import { AppLink } from '@/shared/components/AppLink';

type EditorialCardVariant = 'mobile' | 'desktop';

function EditorialCardContent({
  item,
  variant,
}: {
  item: EditorialItem;
  variant: EditorialCardVariant;
}) {
  const hasButton = item.btn && item.btn.trim() !== '';
  const isMobile = variant === 'mobile';
  const roundedClass = isMobile ? 'rounded-[5px]' : 'rounded-[10px]';
  const containerClass = isMobile
    ? `relative w-full aspect-square overflow-hidden ${roundedClass}`
    : `relative w-full h-full overflow-hidden ${roundedClass}`;
  const imgClass = isMobile
    ? 'w-full h-full object-cover'
    : 'w-full h-full object-cover transform-gpu md:hover:scale-[1.04] transition-transform duration-700 ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none motion-reduce:transform-none';
  const altText = item.alt || item.title;

  return (
    <div className={containerClass}>
      <div className={isMobile ? '' : 'relative w-full h-full overflow-hidden'}>
        <img
          className={imgClass}
          src={resolveImageUrl(item.imageUrl)}
          alt={altText}
          loading={item.lazyload ? 'lazy' : 'eager'}
          decoding="async"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
          }}
        />
        {hasButton && (
          <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent text-center">
            <button className="bg-white text-black text-xs font-medium py-2 px-4 rounded uppercase tracking-wide">
              {item.btn}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

interface EditorialItem {
  title: string;
  imageUrl: string;
  link?: string;
  productId?: string;
  productName?: string;
  categoryName?: string;
  collection?: string;
  btn?: string;
  gridArea?: string;
  lazyload?: string;
  position?: number;
  alt?: string;
  shouldRefresh?: boolean;
}

interface EditorialSectionProps {
  data: Record<string, unknown> & {
    title?: string | string[];
    items?: EditorialItem[];
    dItems?: EditorialItem[];
    desktopItems?: EditorialItem[];
    blankImageUrl?: string;
    gridLayout?: string;
    dGridLayout?: string;
  };
  sectionKey: string;
  eventLabel?: string;
  priority?: number;
}

export default function EditorialSection({
  data,
  sectionKey: _sectionKey,
  eventLabel: _eventLabel,
  priority: _priority,
}: EditorialSectionProps) {
  const items = (data.items || []) as EditorialItem[];
  const dItems = ((data.desktopItems ?? data['d-items'] ?? data.dItems) || []) as EditorialItem[];
  const title = Array.isArray(data.title) ? data.title[0] : data.title;
  const gridLayout = data.gridLayout as string | undefined;
  const dGridLayout = ((data['d-gridLayout'] || data.dGridLayout) as string | undefined);

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <section className="py-8 md:py-12">
      <div className="">
        {/* Section Title */}
        {title && (
          <div className="mb-6 md:mb-8">
            <h2 className="text-base md:text-[24px] font-normal text-center leading-normal md:leading-[30px]">
              <span className="font-primary text-[#9D7792] md:text-[#032C5E]">{title}</span>
            </h2>
          </div>
        )}

        {/* Mobile: 2-column grid layout */}
        <div className="md:hidden">
          <div className="grid grid-cols-3 gap-[5px] px-[5px]" style={{ gridTemplateAreas: gridLayout }}>
            {items.map((item, index) => {
              const hasLink = item.link && item.link.trim() !== '';
              const content = <EditorialCardContent item={item} variant="mobile" />;

              if (hasLink && item.link) {
                return (
                  <div
                    key={`editorial-mobile-${item.title}-${index}`}
                    style={{ gridArea: item.gridArea }}
                  >
                    <AppLink
                      to={item.link}
                      eventName="Editorial Card Clicked"
                      eventProperties={{
                        'Editorial Card Name': item.title,
                        Position: index + 1,
                        'Product Id': item.productId ?? null,
                        'Product Name': item.productName ?? null,
                        'Category Name': item.categoryName ?? null,
                        Collection: item.collection ?? null,
                      }}
                      shouldRefresh={item.shouldRefresh}
                    >
                      {content}
                    </AppLink>
                  </div>
                );
              }

              return (
                <div
                  key={`editorial-mobile-${item.title}-${index}`}
                  style={{ gridArea: item.gridArea }}
                >
                  {content}
                </div>
              );
            })}
          </div>
        </div>

        {/* Desktop: Grid layout using --grid-layout CSS variable */}
        <div className="hidden md:block">
          <div className="container-fluid">
            {dGridLayout && dItems.length > 0 && (
              <div 
                className="grid gap-[10px]"
                style={{
                  '--grid-layout': dGridLayout,
                  gridTemplateAreas: 'var(--grid-layout)',
                } as React.CSSProperties}
              >
                {dItems.map((item, index) => {
                  const hasLink = item.link && item.link.trim() !== '';
                  const gridArea = item.gridArea || '';
                  const content = <EditorialCardContent item={item} variant="desktop" />;

                  if (hasLink && item.link) {
                    return (
                      <AppLink
                        key={`editorial-${item.title}-${index}`}
                        to={item.link}
                        style={gridArea ? { gridArea } : undefined}
                        eventName="Editorial Card Clicked"
                        eventProperties={{
                          'Editorial Card Name': item.title,
                          Position: index + 1,
                          'Product Id': item.productId ?? null,
                          'Product Name': item.productName ?? null,
                          'Category Name': item.categoryName ?? null,
                          Collection: item.collection ?? null,
                        }}
                        shouldRefresh={item.shouldRefresh}
                      >
                        {content}
                      </AppLink>
                    );
                  }

                  return (
                    <div
                      key={`editorial-${item.title}-${index}`}
                      style={gridArea ? { gridArea } : undefined}
                    >
                      {content}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

